/**
 * Loopy Coach panel — Gemini-generated encouragement + 1-3 tips.
 *
 * Pure presentational. The host page owns the API call (loading
 * state, retry, locale) and feeds the result here. Clicking a tip's
 * "看詳細解說 →" link opens the matching prosodyRef / phonicsRef
 * card on /knowledge/prosody (or /knowledge/phonics for
 * mispronunciation) — link target is precomputed server-side and
 * passed in `tip.refLink`, so this component doesn't carry the
 * detector/resolver logic.
 */
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CoachResult } from '../../services/speakingAssessmentService';
import LoopyAvatar, { LoopyEmotion } from '../loopy/LoopyAvatar';
import { pulseGoodJob } from '../../utils/dynamicFavicon';
import './CoachingPanel.css';

export interface CoachingPanelProps {
    loading?: boolean;
    result?: CoachResult;
    /** Overall pronunciation score 0–100. Drives the avatar emotion so
     *  the duck visually reacts to performance instead of always
     *  looking the same regardless of how the learner did. */
    score?: number;
}

const emotionForScore = (score: number | undefined): LoopyEmotion => {
    if (score === undefined) return 'study';
    if (score >= 90) return 'celebrate';     // shining performance
    if (score >= 80) return 'yeah';          // solid, encouraging thumbs-up vibe
    if (score >= 70) return 'study';         // attentive, "let's tighten this up"
    if (score >= 60) return 'point';         // pointing out specifics to fix
    return 'sweat';                          // sub-60: gentle "we'll keep working"
};

const CoachingPanel: React.FC<CoachingPanelProps> = ({ loading, result, score }) => {
    const { t } = useTranslation('learning');
    const title = t('shadowing.coach.title', { defaultValue: 'Loopy Coach' });
    const ariaLabel = t('shadowing.coach.ariaLabel', { defaultValue: title });

    // Pulse the browser-tab favicon to loopy_goodjob whenever the coach
    // hands out a thumbs-up (score ≥ 80, the 'yeah'/'celebrate' tier).
    // Effect re-fires whenever a fresh result arrives, so re-recording
    // with another high score gets another pulse.
    useEffect(() => {
        if (result && typeof score === 'number' && score >= 80) {
            pulseGoodJob();
        }
    }, [result, score]);
    if (loading && !result) {
        return (
            <section className="coaching-panel coaching-panel--loading">
                <header className="coaching-panel__header">
                    <LoopyAvatar size="sm" emotion="thinking" ariaLabel={ariaLabel} />
                    <div className="coaching-panel__title">{title}</div>
                </header>
                <div className="coaching-panel__loading">
                    {t('shadowing.coach.loading', { defaultValue: 'Analyzing your recording⋯' })}
                </div>
            </section>
        );
    }
    if (!result) return null;
    return (
        <section className="coaching-panel">
            <header className="coaching-panel__header">
                <LoopyAvatar size="sm" emotion={emotionForScore(score)} ariaLabel={ariaLabel} />
                <div className="coaching-panel__title">{title}</div>
            </header>
            {result.encouragement && (
                <p className="coaching-panel__encouragement">{result.encouragement}</p>
            )}
            {result.tips.length > 0 && (
                <ul className="coaching-panel__tips">
                    {result.tips.map((tip, idx) => (
                        <li key={`${tip.issue}-${idx}`}>
                            <span className="coaching-panel__marker">{idx + 1}</span>
                            <span className="coaching-panel__advice">
                                {tip.advice}
                                {tip.refLink && (
                                    <>
                                        {' '}
                                        <a
                                            href={tip.refLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="coaching-panel__link"
                                        >
                                            {t('shadowing.coach.refLink', { defaultValue: 'See full explanation →' })}
                                        </a>
                                    </>
                                )}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
};

export default CoachingPanel;
